import { useEffect, useState } from 'react';
import { Lock, Fingerprint, Delete } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import * as candado from '@/hooks/candado';

/**
 * LA PANTALLA DEL CANDADO.
 *
 * Se pone delante de un frente con llave. Dos formas de abrir: la huella del
 * equipo, si la tiene, y un PIN de seis números que siempre está.
 *
 * ═══ SE DICE QUÉ PROTEGE ═══
 *
 * Abajo, en letra chica: protege la pantalla, no los datos. Un candado que
 * promete más de lo que hace es peor que no tenerlo, porque el dueño guarda
 * ahí cosas que cree protegidas.
 */

interface Props {
  nombreDelFrente: string;
  onAbierto: () => void;
  onCancelar: () => void;
}

export function Candado({ nombreDelFrente, onAbierto, onCancelar }: Props) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [conHuella, setConHuella] = useState(false);
  const primeraVez = !candado.hayPin();

  useEffect(() => {
    void candado.hayHuella().then(setConHuella);
  }, []);

  // La huella se ofrece sola al entrar: si el equipo la tiene, pedir el PIN
  // primero es hacerle escribir seis números a quien podía poner el dedo.
  useEffect(() => {
    if (primeraVez || !conHuella) return;
    void candado.pedirHuella().then((ok) => {
      if (ok) {
        candado.abrir();
        onAbierto();
      }
    });
  }, [conHuella, primeraVez, onAbierto]);

  const teclear = async (d: string) => {
    setError('');
    const nuevo = (pin + d).slice(0, 6);
    setPin(nuevo);
    if (nuevo.length < 6) return;

    if (primeraVez) {
      try {
        await candado.ponerPin(nuevo);
        candado.abrir();
        onAbierto();
      } catch (e) {
        setError((e as Error).message);
        setPin('');
      }
      return;
    }

    if (await candado.pinCorrecto(nuevo)) {
      candado.abrir();
      onAbierto();
    } else {
      setError('Ese no es tu PIN');
      setPin('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-5 bg-white px-6 dark:bg-slate-950">
      <span className="grid h-14 w-14 place-items-center rounded-full bg-slate-100 dark:bg-slate-800">
        <Lock className="h-6 w-6 text-slate-500" />
      </span>

      <div className="text-center">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
          {nombreDelFrente}
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {primeraVez ? 'Elige un PIN de 6 números para este candado' : 'Escribe tu PIN'}
        </p>
      </div>

      <div className="flex gap-2">
        {Array.from({ length: 6 }, (_, i) => (
          <span
            key={i}
            className={cn(
              'h-3 w-3 rounded-full transition',
              i < pin.length ? 'bg-acento-lleno' : 'bg-slate-200 dark:bg-slate-700',
            )}
          />
        ))}
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="grid w-full max-w-[260px] grid-cols-3 gap-2">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button
            key={d}
            onClick={() => void teclear(d)}
            className="min-h-[56px] rounded-xl bg-slate-100 text-lg font-medium dark:bg-slate-800"
          >
            {d}
          </button>
        ))}
        {conHuella && !primeraVez ? (
          <button
            onClick={() =>
              void candado.pedirHuella().then((ok) => {
                if (ok) {
                  candado.abrir();
                  onAbierto();
                } else setError('No se pudo verificar');
              })
            }
            aria-label="Usar huella"
            className="grid min-h-[56px] place-items-center rounded-xl bg-slate-100 dark:bg-slate-800"
          >
            <Fingerprint className="h-5 w-5" />
          </button>
        ) : (
          <span />
        )}
        <button
          onClick={() => void teclear('0')}
          className="min-h-[56px] rounded-xl bg-slate-100 text-lg font-medium dark:bg-slate-800"
        >
          0
        </button>
        <button
          onClick={() => {
            setPin((p) => p.slice(0, -1));
            setError('');
          }}
          aria-label="Borrar"
          className="grid min-h-[56px] place-items-center rounded-xl bg-slate-100 dark:bg-slate-800"
        >
          <Delete className="h-5 w-5" />
        </button>
      </div>

      <Button variant="ghost" onClick={onCancelar}>
        Volver
      </Button>

      {/* Lo que el candado NO hace. Prometer más haría que el dueño guarde ahí
          cosas creyéndolas protegidas de alguien que sabe usar un navegador. */}
      <p className="max-w-xs text-center text-[11px] text-slate-400">
        Este candado tapa la pantalla en tu teléfono. No cifra los datos ni los
        esconde de quien tenga tu sesión abierta.
      </p>
    </div>
  );
}
